import { useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Play, RotateCw, CheckCircle2, XCircle, Clock, AlertCircle, Loader2, ExternalLink } from "lucide-react";
import { useFlows } from "@flows/lib/use-flows";
import { Button } from "@/components/ui/button";
import { useRunControl } from "@/context/run-control";
import type { FlowSummary } from "@flows/api/blast-api-types";

type FlowTestStatus = 'idle' | 'running' | 'passed' | 'failed' | 'timeout' | 'error';

interface FlowTestResult {
  flowId: string;
  title: string;
  status: FlowTestStatus;
  startedAt?: number;
  endedAt?: number;
  durationMs?: number;
  errorMessage?: string;
}

export function TestSuiteView() {
  const navigate = useNavigate();
  const { flows, isLoading, error } = useFlows();
  const {
    start, waitForTerminal, runId, status: runStatus, isBusy, nodeErrors, lastNodeError, stop
  } = useRunControl();

  const [results, setResults] = useState<Map<string, FlowTestResult>>(new Map());
  const [suiteRunning, setSuiteRunning] = useState(false);
  const abortRef = useRef(false);
  // Track the current waitForTerminal abort controller so we can cancel it on Abort
  const waitAbortRef = useRef<AbortController | null>(null);

  const formatDuration = (ms?: number) =>
    ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;

  const getLatestErrorMessage = (): string | undefined => {
    // Prefer the most recent node error captured by the provider
    if (lastNodeError?.message) return lastNodeError.message;

    // Fallback: find the latest error across all nodes by timestamp
    let latest: { ts: number; msg: string } | undefined;
    for (const list of nodeErrors.values()) {
      for (const e of list) {
        if (!latest || e.ts > latest.ts) {
          latest = { ts: e.ts, msg: e.message };
        }
      }
    }
    return latest?.msg;
  };

  const setResult = (flowId: string, patch: Partial<FlowTestResult>) => {
    setResults(prev => {
      const next = new Map(prev);
      const curr = next.get(flowId) ?? {
        flowId,
        title: flows.find(f => f.id === flowId)?.title || flowId,
        status: 'idle' as FlowTestStatus
      };
      next.set(flowId, { ...curr, ...patch });
      return next;
    });
  };

  const runOne = useCallback(async (flow: FlowSummary, timeoutMs = 120_000): Promise<FlowTestStatus> => {
    console.log('runeOne start', flow);
    if (abortRef.current) return 'error';

    const startedAt = Date.now();
    setResult(flow.id, { status: 'running', startedAt, endedAt: undefined, durationMs: undefined, errorMessage: undefined });

    // Create an AbortController to allow abortAll() to cancel the wait immediately
    const ac = new AbortController();
    waitAbortRef.current = ac;

    try {
      await start(flow.id);
      const term = await waitForTerminal({ timeoutMs, signal: ac.signal });
      const endedAt = Date.now();
      const durationMs = endedAt - startedAt;

      if (term === 'completed') {
        setResult(flow.id, { status: 'passed', endedAt, durationMs });
        return 'passed';
      } else if (term === 'failed') {
        const errorMessage = getLatestErrorMessage() || 'Run failed';
        setResult(flow.id, { status: 'failed', endedAt, durationMs, errorMessage });
        return 'failed';
      } else {
        const errorMessage = `Unexpected terminal state: ${term}`;
        setResult(flow.id, { status: 'error', endedAt, durationMs, errorMessage });
        return 'error';
      }
    } catch (e: any) {
      const endedAt = Date.now();
      const durationMs = endedAt - startedAt;

      let status: FlowTestStatus;
      if (e?.name === 'AbortError') {
        status = 'error'; // Could add explicit 'aborted' status if desired
      } else if (e?.message && e.message.includes('Timeout')) {
        status = 'timeout';
      } else {
        status = 'error';
      }

      setResult(flow.id, {
        status,
        endedAt,
        durationMs,
        errorMessage: e?.message || 'Unknown error'
      });
      return status;
    } finally {
      console.log('runeOne end', flow);
      // Clear current abort controller
      if (waitAbortRef.current === ac) {
        waitAbortRef.current = null;
      }
    }
  }, [start, waitForTerminal, getLatestErrorMessage, flows, stop]);

  const runAll = useCallback(async () => {
    if (suiteRunning || isLoading) return;

    abortRef.current = false;
    setSuiteRunning(true);

    // Reset all results
    setResults(new Map(flows.map(f => [f.id, {
      flowId: f.id,
      title: f.title || f.id,
      status: 'idle' as FlowTestStatus
    }])));

    for (const flow of flows) {
      if (abortRef.current) break;
      await runOne(flow);
    }

    setSuiteRunning(false);
  }, [suiteRunning, isLoading, flows, runOne]);

  const retryFailed = useCallback(async () => {
    if (suiteRunning || isLoading) return;

    abortRef.current = false;
    setSuiteRunning(true);

    const failedFlows = flows.filter(f => {
      const r = results.get(f.id);
      return r && (r.status === 'failed' || r.status === 'error' || r.status === 'timeout');
    });

    for (const flow of failedFlows) {
      if (abortRef.current) break;
      await runOne(flow);
    }

    setSuiteRunning(false);
  }, [suiteRunning, isLoading, flows, results, runOne]);

  const abortAll = () => {
    abortRef.current = true;
    setSuiteRunning(false);

    // Abort the current waitForTerminal and stop the active run immediately
    if (waitAbortRef.current) {
      waitAbortRef.current.abort();
      waitAbortRef.current = null;
    }
    if (runId) {
      void stop(); // stop() clears run and rejects waiters with AbortError
    }
  };

  const hasFailed = useMemo(() => {
    return Array.from(results.values()).some(r =>
      r.status === 'failed' || r.status === 'error' || r.status === 'timeout'
    );
  }, [results]);

  const getStatusElement = (status: FlowTestStatus) => {
    switch (status) {
      case 'passed':
        return <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Passed</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="h-4 w-4" /> Failed</span>;
      case 'error':
        return <span className="inline-flex items-center gap-1 text-red-600"><AlertCircle className="h-4 w-4" /> Error</span>;
      case 'timeout':
        return <span className="inline-flex items-center gap-1 text-amber-600"><Clock className="h-4 w-4" /> Timeout</span>;
      case 'running':
        return <span className="inline-flex items-center gap-1 text-blue-600"><Loader2 className="h-4 w-4 animate-spin" /> Running</span>;
      default:
        return <span className="text-muted-foreground">Idle</span>;
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Test Suite</h1>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={runAll}
            disabled={suiteRunning || isBusy || isLoading}
          >
            {suiteRunning ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Play className="h-4 w-4 mr-1" />
            )}
            Run All
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={retryFailed}
            disabled={suiteRunning || isBusy || isLoading || !hasFailed}
          >
            <RotateCw className="h-4 w-4 mr-1" />
            Retry Failed
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={abortAll}
            disabled={!suiteRunning && !runId}
            title={!suiteRunning && !runId ? 'No active run' : 'Abort current run and stop the suite'}
          >
            Abort
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading flows…
        </div>
      )}

      {error && (
        <div className="text-sm text-destructive flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2 w-[28px]">#</th>
              <th className="text-left px-3 py-2">Flow</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Duration</th>
              <th className="text-left px-3 py-2">Error</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {flows.map((flow, index) => {
              const result = results.get(flow.id) ?? {
                flowId: flow.id,
                title: flow.title || flow.id,
                status: 'idle' as FlowTestStatus
              };

              return (
                <tr key={flow.id} className="border-t">
                  <td className="px-3 py-2 align-top">{index + 1}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium">{flow.title ?? flow.id.split("/").slice(-1)[0]}</div>
                    <div className="text-xs text-muted-foreground">{flow.id}</div>
                  </td>
                  <td className="px-3 py-2 align-top">{flow.category ?? "—"}</td>
                  <td className="px-3 py-2 align-top">{getStatusElement(result.status)}</td>
                  <td className="px-3 py-2 align-top">{formatDuration(result.durationMs)}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="max-w-[420px] truncate" title={result.errorMessage}>
                      {result.errorMessage ?? "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/flows/${encodeURIComponent(flow.id)}`)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              );
            })}
            {flows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  No flows available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Debug footer with current run info */}
      {runId && (
        <div className="text-xs text-muted-foreground">
          Active run: <span className="font-mono">{runId}</span> ({runStatus})
        </div>
      )}
    </div>
  );
}

export default TestSuiteView;
